import * as React from 'react';
import Loader from 'src/components/common/Loader';
import { CustomFields, StylesProps } from 'src/Library/Types';
import { styles } from './SEDetails.styles';
import moment, { MomentInput } from 'moment';
import { SEOrgType, SEUserPermissions } from '../../SE.interfaces';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import GenericHoc from 'src/components/hoc/GenericHoc';
import GetExecutionData from 'src/dto/response/getExecution';
import Tooltip from 'antd/lib/tooltip';
import Icon from 'antd/lib/icon';
import { Table } from 'antd/lib';
import useVisibility from 'src/hooks/useVisibility';
import Modal from 'antd/lib/modal';
import { deleteShipment, getShipmentDetails, holdShipment, releaseShipment, getShipmentContainers } from 'src/api/shipmentExecution';
import { Execution } from 'src/Library/MasterDataType';
import { CustomField, ExecutionDetails } from './shipment-details.types';
import message from 'antd/lib/message';
import { MilestoneCodes, ProcurementTypeLabels, RelianceCompanyCodeMapping, isBreakBulkMode } from '../../SE.constants';
import { InquiryModes, InquiryType, ModesOfService } from 'src/Library/Constants';
import Input from 'antd/lib/input';
import { useState } from 'react';
import { ShipmentFormFields } from '../../create-shipment/new-shipment.constants';
import DelinkContainers from './SEDelinkContainers';
import classNames from 'classnames';
import { isJSON, isValidArray } from 'src/Library/Helper';
import startCase from 'lodash/startCase';
import _ from 'lodash';
import FailureArrayHandleRfq from 'src/components/common/FailureArrayHandleRfq';
import { isValidDate } from 'src/utils/utils';

interface IProps extends StylesProps<typeof styles> {
  volumeConverterMap;
  domesticVehicleTypes;
  data: GetExecutionData;
  permissions: SEUserPermissions;
  executionMasterData: Execution;
  se_disable_gross_weight_calculation: boolean;
  type: SEOrgType;
  selectedShipment: GetExecutionData;
  isParentOrg: boolean;
  isChildOrg: boolean;
  showEdit: (details) => void;
  resetFilters: () => void;
}

const SEDetails = (props: IProps) => {
  const { classes, permissions, executionMasterData, type, selectedShipment, volumeConverterMap, domesticVehicleTypes,se_disable_gross_weight_calculation } = props;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [details, setDetails] = React.useState<ExecutionDetails>();
  const [delinkContainersClicked, setDelinkContainersClicked] = React.useState<boolean>(false);
  const [shipmentContainers, setShipmentContainers] = useState<
    { containerNumber: string; id: string }[]
  >([]);
  const [showMore, setShowMore] = useState({});
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorArrayData, setErrorArrayData] = useState([] as any);
  
  const modals = useVisibility(['delete', 'edit', 'hold', 'release']);

  const { showEdit, resetFilters } = props;

  const { TRANSPORTER, CHA, CFS, ICD, SURVEYOR, BREAK_BULK, INTER_CARTING } = ShipmentFormFields;
  
  const VOLUMETRIC_WT_DENOMINATOR = 6000;

  async function loadContainers() {
    const result = await getShipmentContainers(selectedShipment.referenceNumber);
    if (result.isSuccess) {
      setShipmentContainers(result?.data || []);
    }
  }

  React.useEffect(() => {
    loadContainers();
  }, [selectedShipment.id]);

  async function loadData() {
    setLoading(true);
    const result = await getShipmentDetails({ shipmentId: selectedShipment.id, viewType: type }) as any;
    if (result.isSuccess) {
      setDetails(result?.data || {});
    }
    setLoading(false);
  }

  React.useEffect(() => {
    loadData();
  }, [selectedShipment.id]);

  const [deletionReason, setDeletionReason] = useState<string | undefined>();
  const renderDeletionModalContent = () => {
    return (
      <Input.TextArea
        placeholder="Enter reason for deletion"
        onChange={(e) => setDeletionReason(e.target.value)}
      />
    );
  }

  const handleErrorModal = () => {
    setIsErrorModalVisible(!isErrorModalVisible);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const response = await deleteShipment({ shipmentIdList: [details?.id], deletionReason });
    if (response.isSuccess) {
      if (response.data?.failureArray?.length) {
        const failureArray = response?.data?.failureArray?.map((obj) => ({
          objectId: obj.id,
          reasons: [{message: obj.error_message}]          
        }))
        setErrorArrayData(failureArray || []);
        setIsErrorModalVisible(true);
      } else {
        resetFilters();
        modals.delete.hide();
      }
    } else {
      message.error(response.errorMessage)
    }
    setDeleting(false);
  };

  const handleHold = async () => {
    const res: any = await holdShipment({ shipmentId: details?.id });
    if (res.isSuccess) {
      message.success('Shipment put on hold successfully.');
      loadData();
      resetFilters();
    } else {
      message.error(res.errorMessage)
    }
  };

  const handleRelease = async () => {
    const res: any = await releaseShipment({ shipmentId: details?.id });
    if (res.isSuccess) {
      message.success('Shipment released successfully.');
      loadData();
      resetFilters();
    } else {
      message.error(res.errorMessage)
    }
  };

  const renderPOLDetails = () => {
    if (details?.originPolCode) {
      return (
        <div className={classes.value}>
          <strong>
            {details?.originPortCode
              ? `${details?.originPortName},
      ${details?.originPortCode}
       (ICD) `
              : '-'}
            <br />
          </strong>
          <span style={{ fontSize: '12px', color: '#262626' }}>Wet Port </span>
          <strong>
            {details?.originPolCode ? `${details?.originPolName}, ${details?.originPolCode}` : '-'}
          </strong>
        </div>
      );
    }
    return (
      <div className={classes.value}>
        <strong>
          {details?.originPortCode ? `${details?.originPortName}, ${details?.originPortCode} ` : '-'}
          {details?.originPolCode ? '(ICD)' : ''}
        </strong>
      </div>
    );
  };

  const renderPODDetails = () => {
    if (details?.destinationPodCode) {
      return (
        <div className={classes.value}>
          <strong>
            {details?.destinationPortCode
              ? `${details?.destinationPortName}, ${details?.destinationPortCode}
       (ICD) `
              : '-'}
            <br />
          </strong>
          <span style={{ fontSize: '12px', color: '#262626' }}>Wet Port </span>
          <strong>
            {details?.destinationPodCode
              ? `${details?.destinationPodName}, ${details?.destinationPodCode}`
              : '-'}
          </strong>
        </div>
      );
    }
    return (
      <div className={classes.value}>
        <strong>
          {details?.destinationPortCode
            ? `${details?.destinationPortName}, ${details?.destinationPortCode} `
            : '-'}
          {details?.destinationPodCode ? '(ICD)' : ''}
        </strong>
      </div>
    );
  };

  const renderLine = () => {
    return <div className={classes.line}/>;
  };

  const renderContentTitle = (title: string) => {
    return <div 
      className={classes.contentTitle}>
      {title}
    </div>;
  };

  const renderTitle = (title: string) => {
    return <div 
      className={classes.title}>
      {title}
    </div>;
  };

  const returnFormattedAddress = (location) => {
    return [
      location?.name, 
      location?.address, 
      location?.landmark, 
      location?.city, 
      location?.state, 
      location?.countryName, 
      location?.pincode
    ].filter((val) => val).join(', ');
  }

  const renderJsonArray = (parsedValue, key) => {
    if (!parsedValue || parsedValue.length <= 0) return;
    if (!showMore[key]) {
      const obj = parsedValue[0];
      return (
        <div>

          <div className={classes.value} style={{ marginBottom: 4 }}>
            {Object.keys(obj).map((key) => (
              <div>
                {startCase(key)}: {obj[key] || '-'}
              </div>
            ))}
          </div>
          {parsedValue.length > 1 && renderShowMore(key)}
        </div>
      );
    }

    return (
      <div>
        {parsedValue.map((obj) => {
          return (
            <div className={classes.value} style={{ marginBottom: 4 }}>
              {Object.keys(obj).map((key) => (
                <div>
                  {startCase(key)}: {obj[key] || '-'}
                </div>
              ))}
            </div>
          );
        })}
        {renderShowMore(key)}
      </div>
    );
  };
  
  const renderJsonValues = (value, key) => {
    const parsedValue = JSON.parse(value as string);

    if (parsedValue && typeof parsedValue !== 'object') {
      return <div className={classes.value}>{value}</div>;
    }

    if (!isValidArray(parsedValue) && !parsedValue?.length) {
      return <div className={classes.na}>-</div>;
    }
    return renderJsonArray(parsedValue, key);
  };


  const renderValue = (value: string | number | React.ReactNode | undefined, isDate: boolean = false, key?: string | number) => {
    if (!value || value === 'N.A.') {
      return <div
        className={classes.na}>
        -
      </div>;
    }
    else if (React.isValidElement(value)) {
      return value;
    }
    else if (isDate) {
      return <div
        className={classes.value}>
        {moment(React.isValidElement(value) ? '-' : value).format('DD/MM/YYYY | HH:mm A')}
      </div>;
    }
    else if (isJSON(value) && key) {
      return renderJsonValues(value, key);
    }

    return <div className={classes.value}>{value}</div>;
  };

  const renderShowMore = (key: string | number) => {
    return (
      <div
        className={classes.showMoreText}
        onClick={() => setShowMore({ ...showMore, [key]: !showMore[key] })}
      >
        {showMore[key] ? <span>Show less</span> : <span>Show more</span>}
      </div>
    );
  }

  const renderField = (
    width: string,
    title: string,
    value: string | number | React.ReactNode | undefined,
    isDate: boolean = false,
    key?: string | number,
  ) => {
    return <div
      className={classes.flexRow}
      style={{ width, alignItems: 'normal' }}>
      {renderTitle(title)} {renderValue(value, isDate, key)}
    </div>;
  };

  const renderOriginServices = () => {
    return (
      <div className={classes.value}>
          {details?.originServices?.length ? (
            details?.originServices.map((item) => {
              return (
                <div key={item}>
                  <strong>{item ? item : '-'}</strong>
                  <br />
                </div>
              );
            })
          ) : (
            <strong>-</strong>
          )}
      </div>
    );
  };

  const renderDestinationServices = () => {
    return (
      <div className={classes.value}>
          {details?.destinationServices?.length ? (
            details?.destinationServices.map((item) => {
              return (
                <div key={item}>
                  <strong style={{ width: 'auto' }}>{item ? item : '-'}</strong>
                  <br />
                </div>
              );
            })
          ) : (
            <strong>-</strong>
          )}
      </div>
    );
  };

  const renderService = () => {
    const width = '33%';
    return <div className={classes.contentBox}>
      {renderContentTitle('Service')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField(width, 'Mode', details?.mode)}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'Incoterm', details?.incoterm)}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'Origin Services', renderOriginServices())}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, 'Type', details?.type || '-')}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'PickUp & Drop', details?.pickupAndDrop)}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'Destination Services', renderDestinationServices())}
      </div>
      <div className={classes.contentRow}>
        {details?.procurementType && renderField(width, 'Procurement Type', ProcurementTypeLabels[details.procurementType])}
      </div>
      <div className={classes.contentRow}>
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(
          width,
          'Carrier',
          details?.carrierLogoUrl ?
          <img
            src={details?.carrierLogoUrl}
            style={{
              width: 'auto',
              marginLeft: 12,
            }}
          /> : '-')}
      </div>
    </div>;
  };

  const renderAddressDetails = () => {
    const width = '50%';
    return <div className={classes.contentBox}>
      {renderContentTitle('Address')}
      {renderLine()}
      <div className={classes.contentRow}>
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'POL', renderPOLDetails())}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, 'POD', renderPODDetails())}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, 'Origin Country', details?.originCountryName)}
        {renderField(width, 'Destination Country', details?.destinationCountryName)}
      </div>
      <div className={classes.contentRow}>
        {renderField(
          width, 
          'Origin Address', 
          (details?.mode === InquiryModes.DOMESTIC || details?.mode === ModesOfService.RPTL) ? returnFormattedAddress(details?.originLocationDetails) : details?.originAddress
        )}
        {renderField(
          width, 
          'Destination Address', 
          (details?.mode === InquiryModes.DOMESTIC || details?.mode === ModesOfService.RPTL) ? returnFormattedAddress(details?.destinationLocationDetails) : details?.destinationAddress
        )}
      </div>
      <div className={classes.contentRow}>
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, details?.mode === 'AIRCARGO' ? 'Loading' : 'Stuffing', details?.originLocationType)}
        {(details?.mode !== InquiryModes.DOMESTIC && details?.mode !== ModesOfService.RPTL) && renderField(width, details?.mode === 'AIRCARGO' ? 'Unloading' : 'Destuffing', details?.destinationLocationType)}
      </div>
      <div className={classes.contentRow}>
        {renderField(
          width, 
          details?.mode === 'AIRCARGO' ? 'Loading Location' : 'Stuffing Location', 
          (details?.mode === InquiryModes.DOMESTIC || details?.mode === ModesOfService.RPTL) ? returnFormattedAddress(details?.originLocationDetails) : details?.originLocationAddress
        )}
        {renderField(
          width, 
          details?.mode === 'AIRCARGO' ? 'Unloading Location' : 'Destuffing Location', 
          (details?.mode === InquiryModes.DOMESTIC || details?.mode === ModesOfService.RPTL) ? returnFormattedAddress(details?.destinationLocationDetails) : details?.destinationLocationAddress
        )}
      </div>
    </div>;
  };

  const getConcatiatedNames = (nameList) => {
    if ( Array.isArray(nameList) ){
      return nameList.join(", ");
    }
    return ""
  }

  const renderStakeholdersDetails = () => {    
    const width = '40%';
    const supplierOrCustomerLabel = details?.type === InquiryType.EXPORT ? 'Customer' : 'Supplier';
    const supplierOrCustomerValue = details?.type === InquiryType.EXPORT ? getConcatiatedNames(details?.customerNames) : getConcatiatedNames(details?.supplierNames);

    return <div className={classes.contentBox}>
      {renderContentTitle('Stakeholders')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField(width, 'FF', details?.ffName)}
        {renderField(width, 'Shipper', details?.shipperName)}
      </div>
      {(props.isParentOrg || props.isChildOrg) && <div className={classes.contentRow}>
        {renderField(width, 'Child', details?.childName )}
        {renderField(width, 'Parent', details?.parentName)}
      </div>}
      <div className={classes.contentRow}>
        {renderField(width, CHA.label, details?.chaName)}
        {renderField(width, TRANSPORTER.label, details?.transporterName)}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, CFS.label, details?.cfsContractorName)}
        {renderField(width, ICD.label, details?.icdContractorName)}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, SURVEYOR.label, details?.surveyorName)}
        {renderField(width, BREAK_BULK.label, details?.breakBulkName)}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, INTER_CARTING.label, details?.interCartingName)}
      </div>
      <div className={classes.contentRow}>
        {(details?.type === InquiryType.EXPORT || details?.type === InquiryType.TRADE) && renderField(width, 'Customer', getConcatiatedNames(details?.customerNames))}
        {(details?.type === InquiryType.IMPORT || details?.type === InquiryType.TRADE) && renderField(width, 'Supplier', getConcatiatedNames(details?.supplierNames))}
      </div>
    </div>;
  };

  const renderAdditionalDetails = () => {
    const width = '40%';
    return <div className={classes.contentBox}>
      {renderContentTitle('Additional Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField(width, 'Purchase Order #', details?.purchaseOrderNumberList?.join(', '))}
        {renderField(width, 'Plant', details?.plantAddress)}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, 'Sales Order #', details?.salesOrderNumber)}
        {renderField(width, 'Shipment Access', <div className={classes.value}>
          <div>
          {details?.accessType === 'PUBLIC' ? (
            <React.Fragment>
              <Icon type="global" /> Public
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Icon type="team" /> Team
            </React.Fragment>
          )}
        </div>
        {details?.accessType === 'TEAM' && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {details?.teamList?.map((item) => {
              return (
                <strong key={item?.name} style={{ paddingRight: 0 }}>
                  {item?.name}
                </strong>
              );
            })}
          </div>
        )}
        </div>)}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, 'Invoice #', details?.invoiceNumberList?.join(', '))}
        {renderField(width, 'Watcher', <div className={classes.value}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(selectedShipment.watcherList || []).map((item) => {
            return (
              <strong key={item} style={{ paddingRight: 0 }}>
                {item}
              </strong>
            );
          })}
        </div>
      </div>)}
      </div>
      <div className={classes.contentRow}>
        {details?.picTeam?.name && renderField(width, 'PIC Team #', details?.picTeam?.name)}
        {details?.picAssignees && renderField(width, 'PIC Assignee #', details?.picAssignees?.join(', '))}
      </div>
    </div>;
  };

  const renderCustomFieldHelper = (field: CustomField, width) => {
    const { name, dbKey, value, type, readonly } = field;
    return (
      <div className={classes.flexRow} style={{ width, alignItems: 'normal' }}>
        {renderTitle(name)}{' '}
        {(() => {
          switch (field.type) {
            case 'Array':
              return renderJsonArray(field.value, dbKey);
            case 'Integer':
              return <div className={classes.value}>{+value}</div>;
            case 'Decimal':
              return <div className={classes.value}>{+value}</div>;
            case 'Object':
              if (value) {
                return renderJsonArray([{ ...value }], dbKey);
              }
              return <></>;
            case 'String':
              return <div className={classes.value}>{value}</div>;
            case 'Date':
              return (
                <div className={classes.value}>
                  {isValidDate(value) ? moment(value).format('DD MMMM YYYY') : (value || '-')}
                </div>
              );
            default:
              return <div className={classes.value}>{value}</div>;
          }
        })()}
      </div>
    );
  };
  
  const renderCustomFields = () => {
    if (!details?.customFields) {
      return;
    }

    const fieldsArray: React.ReactElement[] = [];
    const width = '50%';
    const { customFields } = details || {};

    for (let idx = 0; idx < customFields?.length; idx+=2) {
      const fieldOne = customFields[idx];
      const fieldTwo = customFields[idx+1];

      fieldsArray.push(
        <div className={classes.contentRow}>
          {fieldOne && renderCustomFieldHelper(fieldOne, width)}
          {fieldTwo && renderCustomFieldHelper(fieldTwo, width)}
        </div>
      );
    }

    return fieldsArray;
  }

  const renderOrderDetails = () => {
    if (!details?.customFields?.length) {
      return;
    }

    return <div className={classes.contentBox}>
      {renderContentTitle('Order Details')}
      {renderLine()}
      {renderCustomFields()}
    </div>;
  }

  const renderTermsAndConditions = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Terms & Conditions')}
      {renderLine()}
      <div
        className={classes.value}
        style={{ marginTop: 12 }}>
          {details?.termsAndConditions || '-'}
      </div>
    </div>;
  };

  const renderCargo = () => {
    const { cargoType } = executionMasterData;
    return <div className={classes.value}>
      {details?.cargoType ? cargoType[details.cargoType] : '-'} {details?.cargoSubtype}
    </div>;
  };

  const getTotalVolume = (id) => {
    const length = details?.packagingDetails[id].length;
    const breadth = details?.packagingDetails[id].breadth;
    const height = details?.packagingDetails[id].height;
    const lengthUnit = details?.packagingDetails[id].dimensionUnit;

    if (!lengthUnit) {
      return undefined;
    }

    const volumeUnit = 'cbm';
    const conversionFactor =
      volumeConverterMap?.[lengthUnit] &&
      volumeConverterMap[lengthUnit][volumeUnit] &&
      volumeConverterMap[lengthUnit][volumeUnit];
    const newVol =
      length && length > 0 && breadth && breadth > 0 && height && height > 0 && conversionFactor
        ? length * breadth * height * conversionFactor
        : undefined;
    return newVol;
  };

  const renderTotalGrossWeight = (id) => {
    const grossWeight = details?.packagingDetails[id].grossWeight;
    const quantity = details?.packagingDetails[id].count || 0;
    const unit = details?.packagingDetails[id].grossWeightUnit;
    return grossWeight || grossWeight === 0
      ? `${grossWeight && (grossWeight * quantity).toFixed(2)} ${unit}`
      : undefined;
  };

  const renderTotalVolumetricWeight = (id) => {
    const count = details?.packagingDetails[id].count || 0;

    const newVol = getTotalVolume(id);
    const volWeight = newVol
      ? +((newVol * 1000000) / VOLUMETRIC_WT_DENOMINATOR).toFixed(2)
      : undefined;
    const totalVolWeight = volWeight ? +(volWeight * count).toFixed(2) : 0;
    return `${totalVolWeight} kg`;
  };

  const renderPackageDetails = () => {
    const width = '40%';
    const { validContainerOrPackageTypesBasedOnMode } = executionMasterData;
    // Break Bulk mirrors LCL: resolve package-type labels against LCL when master data has no Break Bulk key.
    const lookupMode = isBreakBulkMode(details?.mode) ? ModesOfService.LCL : details?.mode;
    const modeToContainerTypeMap = lookupMode ? validContainerOrPackageTypesBasedOnMode[lookupMode] : '';

    if (details?.mode === 'FCL') {
      return renderField(width, 'Container Details', <div className={classes.value}>
        {details?.packagingDetails?.length
          ? details?.packagingDetails?.map((container: any, index: number) => {
            const constainerType = modeToContainerTypeMap && container.type ? modeToContainerTypeMap[container.type] : '';
            return (
              <div key={container?.type + container?.count}>
                {container.temperature ? (
                  <div>
                    {container.count} {container.size}{' '}
                    {constainerType}  {!_.isNil(container.totalWeight) && !_.isNil(container.totalWeightUnit) &&  
                    `| Total Weight (${container.totalWeight || '-'} ${container.totalWeightUnit || ''})`}
                    <br />
                    {`[${container.temperature}]`}
                  </div>
                ) : (
                    <div>
                      {container.count}X {container.size}{' '}
                      {constainerType} {!_.isNil(container.totalWeight) && !_.isNil(container.totalWeightUnit) && 
                       `| Total Weight (${container.totalWeight || '-'} ${container.totalWeightUnit || ''})`}
                    </div>
                  )}
              </div>
            );
          })
          : '-'}
      </div>
      )
    }

    if (details?.mode === InquiryModes.DOMESTIC) {
      return renderField(width, 'Vehicle Details', <div className={classes.value}>
        {details?.packagingDetails?.length
          ? details?.packagingDetails?.map((container: any, index: number) => {
            const constainerType = container.type ? domesticVehicleTypes[container.type] : '';
            return (
              <div key={container?.type + container?.count}>
                {container.temperature ? (
                  <div>
                    {container.count} {container.size}{' '}
                    {constainerType}  {!_.isNil(container.totalWeight) && !_.isNil(container.totalWeightUnit) &&  
                    `| Total Weight (${container.totalWeight || '-'} ${container.totalWeightUnit || ''})`}
                    <br />
                    {`[${container.temperature}]`}
                  </div>
                ) : (
                    <div>
                      {container.count} X {container.size}{' '}
                      {constainerType} {!_.isNil(container.totalWeight) && !_.isNil(container.totalWeightUnit) && 
                       `| Total Weight (${container.totalWeight || '-'} ${container.totalWeightUnit || ''})`}
                    </div>
                  )}
              </div>
            );
          })
          : '-'}
      </div>
      )
    }

    if (details?.mode === 'LCL' || details?.mode === ModesOfService.RPTL || isBreakBulkMode(details?.mode)) {
      return renderField(width, 'Package Details', <div className={classes.value}>
        {details?.packagingDetails?.length
          ? details.packagingDetails.map((container: any) => {
            const constainerType = modeToContainerTypeMap && container.type ? modeToContainerTypeMap[container.type] : '';
            return (
              <div key={container?.type + container?.count}>
                {container.dimensionUnit ? (
                  <div>
                    {container.count}{' '}
                    {constainerType}
                    {container.otherType ? ` (${container.otherType}), ` : ', '}
                    {container.length} x {container.breadth} x {container.height}{' '}
                    {container.dimensionUnit}, {container.grossWeight}{' '}
                    {container.grossWeightUnit} ({container.minVolume} {container.minVolumeUnit}
                    )
                  </div>
                ) : (
                    <div>
                      {container.count}{' '}
                      {constainerType}
                      {container.otherType ? ` (${container.otherType}), ` : ', '}
                      {container.grossWeight} {container.grossWeightUnit} ({container.minVolume}{' '}
                      {container.minVolumeUnit})
                    </div>
                  )}
              </div>
            );
          })
          : '-'}
      </div>
      )
    }
    return renderField(width, 'Package Details', <div className={classes.value}>
      {details?.packagingDetails?.length
        ? details.packagingDetails.map((container: any, id) => {
          const constainerType = modeToContainerTypeMap && container.type ? modeToContainerTypeMap[container.type] : '';
          return (
            <div key={container?.type + container?.count}>
              <strong>
                {container.count}{' '}
                {constainerType}
                {container.otherType ? ` (${container.otherType}), ` : ', '}
                {container.length} x {container.breadth} x {container.height}{' '}
                {container.dimensionUnit}, {se_disable_gross_weight_calculation ? (
  <>
    {container.totalWeight}{' '}
    {container.totalWeightUnit}
  </>
) : (
  <>
    {container.grossWeight}{' '}
    {container.grossWeightUnit}
  </>
)} ({getTotalVolume(id)?.toFixed(2)} cbm) |
              </strong>
              <span style={{ fontStyle: 'italic' }}>
                (Total Gross Wt {renderTotalGrossWeight(id)} | Total Volumetric Wt{' '}
                {renderTotalVolumetricWeight(id)})
                </span>
            </div>
          );
        })
        : '-'}
    </div>
    );
  }

  const renderCount = (product) => {
    if (!_.isNil(product.count)) {
      return `| Count: ${product.count} `
    }
    return '';
  }

  const renderContentsAndDimensions = () => {
    const width = '40%';
    return <div className={classes.contentBox}>
      {renderContentTitle('Contents & Dimensions')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField(width, 'Cargo', renderCargo())}
        {renderPackageDetails()}
      </div>
      <div className={classes.contentRow}>
        {renderField(width, 'Product',  <div className={classes.value}>
          {details?.productDetails?.length
            ? details?.productDetails.map((item) => (
                <strong key={item?.name + item?.count}>
                  <div style={{marginBottom: '5px'}}>
                    {item.name}{' '}
                    {item.count || item.unit
                      ? `| ${item.count ? item.count : ''} ${item.unit ? item.unit : ''}`
                      : ''}
                    {renderCount(item)}
                    {item.skuClientNumber ? `| SKU client number: ${item.skuClientNumber} ` : ''}
                    {item.cases ? `| Cases: ${item.cases} ` : ''}
                  </div>
                </strong>
              ))
            : '-'}
        </div>
        )}
      </div>
    </div>;
  };

  const renderDocuments = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Documents')}
      {renderLine()}
      <div style={{ display: 'flex', margin: '12px 0px' }}>
        <div className={classes.title} style={{ width: '5%' }}>S No.</div>
        <div className={classes.title} style={{ width: '15%' }}>Version</div>
        <div className={classes.title} style={{ width: '30%' }}>Description</div>
        <div className={classes.title} style={{ width: '5%' }}>Validity</div>
      </div>
      {details?.documents?.map((item, idx) => (<div style={{
        marginBottom: 10,
        backgroundColor: '#FCFAF4',
        padding: 8,
      }}>
        <div className={classes.value} style={{ width: '5%' }}>{idx + 1}</div>
        <div className={classes.value} style={{ width: '15%' }}>{item?.documentVersion}</div>
        <div className={classes.value} style={{ width: '30%' }}>{item?.documentDescription}</div>
        <div className={classes.value} style={{ width: '5%' }}>{item?.validity}</div>
      </div>))}
    </div>;
  };

  const handleCloseDelinkContainers = () => {
    setDelinkContainersClicked(false);
  }

  const renderDelinkContainers = () => {
    if (!delinkContainersClicked) {
      return;
    }
    return (
      <DelinkContainers
        handleCloseDelinkContainers={handleCloseDelinkContainers}
        selectedShipment={selectedShipment}
        shipmentContainers={shipmentContainers}
        viewType={type}
        loadData={loadData}
        loadContainers={loadContainers}
      />
    );
  };

  const showEditModal = () => {
    const { isEditable } = details || {};
    if (!isEditable) {
      return;
    }
    showEdit(details);
  };
  const renderActions = () => {
    const onHold: boolean = details?.milestone === MilestoneCodes.ON_HOLD.key;
    const deleted: boolean = details?.milestone === MilestoneCodes.DELETED.key;
    if (!permissions?.SHIPMENT_ADD_EDIT) {
      return null;
    }

    const { isEditable } = details || {};
    return (
      <div className={classes.flexRow}>
        
        <Tooltip title={shipmentContainers.length ? '' : 'No Container to delink'}>
          <div
            style={{ marginRight: 15 }}
            className={classNames(
              classes.delinkContainersCommon,
              isEditable && !onHold && !deleted && shipmentContainers.length && classes.delinkContainersEnabled,
              !(isEditable && !onHold && !deleted && shipmentContainers.length) && classes.delinkContainersDisabled
            )}
            onClick={
              isEditable && !onHold && !deleted && shipmentContainers.length
                ? () => {
                    setDelinkContainersClicked(true);
                  }
                : undefined
            }
          >
            Delink Containers
          </div>
        </Tooltip>

        {onHold ? (
          <a
            onClick={isEditable && !deleted ? () => modals.release.show() : undefined}
            style={{
              color: isEditable && !deleted ? '#1890FF' : '#d9d9d9',
              cursor: isEditable && !deleted ? 'pointer' : 'not-allowed',
              marginRight: 15,
              fontSize: 12,
            }}
          >
            Release
          </a>
        ) : (
          <a
            onClick={isEditable && !deleted ? () => modals.hold.show() : undefined}
            style={{
              color: isEditable && !deleted ? '#1890FF' : '#d9d9d9',
              cursor: isEditable && !deleted ? 'pointer' : 'not-allowed',
              marginRight: 15,
              fontSize: 12,
            }}
          >
            Hold
          </a>
        )}

        <Tooltip title="Edit Shipment">
          <Icon
            type="edit"
            onClick={isEditable && !onHold && !deleted ? () => showEdit(details) : undefined}
            style={{
              color: isEditable && !onHold && !deleted ? '#262626' : '#d9d9d9',
              cursor: isEditable && !onHold && !deleted ? 'pointer' : 'not-allowed',
            }}
          />
        </Tooltip>

        <Tooltip title="Delete Shipment">
          <Icon
            type="delete"
            onClick={isEditable && !onHold && !deleted ? () => modals.delete.show() : undefined}
            style={{
              margin: '0px 16px',
              color: isEditable && !onHold && !deleted ? '#262626' : '#d9d9d9',
              cursor: isEditable && !onHold && !deleted ? 'pointer' : 'not-allowed',
            }}
          />
        </Tooltip>
      </div>
    );
  };

  const renderReferenceDate = (title: string, value: MomentInput) => {
    return <div className={classes.flexRow}>
      <span className={classes.title} style={{ minWidth: 64 }}>
        {title}
      </span>
      <span className={classes.value} style={{ width: '55%' }}>
        {moment(value).format('DD MMM YYYY')}
      </span>
    </div>;
  };

  const renderReference = () => {
    return <div className={classes.reference}>
      <div className={classes.referenceList}>
        <div className={classes.referenceValue}>{details?.clientReferenceNumber || '-'}</div>
        <div className={classes.referenceTitle}>Internal Number</div>
      </div>
      <div className={classes.referenceList}>
        <div className={classes.referenceValue}>{details?.bookingNumber || '-'}</div>
        <div className={classes.referenceTitle}>Booking Number</div>
      </div>
      <div className={classes.referenceList}>
        <div className={classes.referenceValue}>{details?.bookingReferenceNumber || '-'}</div>
        <div className={classes.referenceTitle}>Shipsy Booking ID</div>
      </div>
      <div style={{ width: '23%' }}>
        {renderReferenceDate('Created at', selectedShipment.createdAt || details?.createdAt)}
        {renderReferenceDate('Updated at', selectedShipment.updatedAt || details?.updatedAt)}
      </div>
      {renderActions()}
    </div>;
  };

  const renderConfirmDelete = () => {
    if (!modals.delete.isVisible) return null;
    return <Modal
      visible={modals.delete.isVisible}
      onCancel={() => modals.delete.hide()}
      onOk={() => {
        if (!deletionReason?.length) {
          message.error('Deletion Reason is mandatory.')
          return;
        }
        handleDelete();
      }}
      confirmLoading={deleting}
      title="Reason for Deletion"
      okText="Confirm"
      width="32%"
    >
      {renderDeletionModalContent()}
    </Modal>;
  };

  const renderConfirmHold = () => {
    return <Modal
    visible={modals.hold.isVisible}
    style={{ minWidth: '300px' }}
    onCancel={() => modals.hold.hide()}
    onOk={() => {
      handleHold();
      modals.hold.hide();
      // fetchAllShipment(type);
    }}
    title="Confirm Hold"
    okText="Confirm"
    width="20%"
    >
    Are you sure you want to hold this shipment?
    </Modal>;
  };

  const renderConfirmRelease = () => {
    return (
      <Modal
        visible={modals.release.isVisible}
        style={{ minWidth: '300px' }}
        onCancel={() => modals.release.hide()}
        onOk={() => {
          handleRelease();
          modals.release.hide();
        }}
        title="Confirm Release"
        okText="Confirm"
        width="20%"
      >
        Are you sure you want to release this shipment?
      </Modal>
    );
  };

  const renderModals = () => {
    return (
      <React.Fragment>
        {modals.delete.isVisible && renderConfirmDelete()}
        {modals.hold.isVisible && renderConfirmHold()}
        {modals.release.isVisible && renderConfirmRelease()}
      </React.Fragment>
    );
  };

  const renderDeletionReason = () => {
    if (!details?.deletionReason) {
      return null;
    }

    return (
      <div className={classes.contentBox}>
        {renderContentTitle('Deletion Reason')}
        {renderLine()}
        <div style={{ display: 'flex', margin: '12px 0px' }}>
          <div className={classes.title}>{details?.deletionReason}</div>
        </div>
      </div>
    );
  }

  const getCompanyLabel = () => {
    if (!details?.companyCode) {
      return;
    }
    
    return RelianceCompanyCodeMapping.find(item => item.key === details.companyCode)?.label;
  }

  const renderCompanyCode = () => {
    const companyLabel = getCompanyLabel();
    if (!companyLabel) {
      return;
    }
    return (
      <div>
        <span className={classes.subHeaderTitle}>Company Code</span>
        <span className={classes.companyLabelText}>{companyLabel}</span>
      </div>
    );
  };

  const renderSubHeader = () => {
    return (
      <div className={classNames(classes.contentBox, classes.subHeaderContainer)}>
        <div>
          <span className={classes.subHeaderTitle}>Shipment Reference</span>
          {details?.referenceNumber ? (
            <span className={classes.referenceNumberText}>{details.referenceNumber}</span>
          ) : (
            <span className={classes.companyLabelText}>-</span>
          )}
        </div>
        {renderCompanyCode()}
      </div>
    );
  }

  const renderFailureArrayModal = () => {
    if (!isErrorModalVisible) return null;
    return (
      <FailureArrayHandleRfq
        id={'SHIPMENT_ID'}
        failureArray={errorArrayData}
        isVisible={isErrorModalVisible}
        onModalClose={() => handleErrorModal()}
      ></FailureArrayHandleRfq>
    );
  };

  const renderContent = () => {
    if (!details?.id || loading) {
      return <Loader
        style={{ marginLeft: '0px' }}
        zIndex={1}
      />;
    }

    return <React.Fragment>
      {renderReference()}
      {renderSubHeader()}
      {renderService()}
      {renderAddressDetails()}
      {renderStakeholdersDetails()}
      {renderContentsAndDimensions()}
      {renderTermsAndConditions()}
      {renderAdditionalDetails()}
      {renderOrderDetails()}
      {renderDocuments()}
      {renderDeletionReason()}
      {renderModals()}
      {renderDelinkContainers()}
      {renderFailureArrayModal()}
    </React.Fragment>;
  };

  return <div className={classes.main}>
    {renderContent()}
  </div>;
};

const mapStateToProps = ({ masterData, inquiryReducer }) => ({
  se_disable_gross_weight_calculation: masterData?.modules?.se_disable_gross_weight_calculation,
  executionMasterData: masterData.execution,
  permissions: masterData.permissions,
  volumeConverterMap: inquiryReducer.masterData.volumeConverterMap,
  domesticVehicleTypes: inquiryReducer.masterData.domesticVehicleTypes,
  isParentOrg: masterData.isParentOrg,
  isChildOrg: masterData.isChildOrg,
});

const options: HocOptions = {
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
};

const SEDetailsHOC = GenericHoc(options)(SEDetails);
export default SEDetailsHOC;
